import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Logo } from "@/components/layout/Logo";
import { Users, Lock, Plus, ArrowLeft } from "lucide-react";

interface Group {
  id: string;
  name: string;
  description?: string;
  memberCount: number;
  maxMembers: number;
}

interface GroupSelectScreenProps {
  groups: Group[];
  onJoin: (groupId: string, password: string) => Promise<void>;
  onCreate: (name: string, password: string) => Promise<void>;
  onSkip: () => void;
}

export function GroupSelectScreen({ groups, onJoin, onCreate, onSkip }: GroupSelectScreenProps) {
  const [mode, setMode] = useState<"list" | "join" | "create">("list");
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [password, setPassword] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupPassword, setGroupPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleJoin = async () => {
    if (!selectedGroup) return;
    setLoading(true);
    setError("");
    try {
      await onJoin(selectedGroup.id, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Katilim basarisiz");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    setError("");
    try {
      await onCreate(groupName, groupPassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Olusturma basarisiz");
    } finally {
      setLoading(false);
    }
  };

  if (mode === "join" && selectedGroup) {
    return (
      <div className="min-h-screen bg-background p-6">
        <button onClick={() => setMode("list")} className="flex items-center gap-2 text-muted-foreground mb-6 hover:text-foreground transition-colors">
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Geri</span>
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gold/10 flex items-center justify-center mx-auto mb-4">
            <Lock className="text-gold" size={28} />
          </div>
          <h2 className="text-xl font-bold">{selectedGroup.name}</h2>
          <p className="text-muted-foreground text-sm mt-1">Katilmak icin sifre girin</p>
        </div>

        <div className="space-y-4">
          <Input
            label="Grup Sifresi"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Sifre"
          />
          {error && <p className="text-ehille-red text-sm text-center">{error}</p>}
          <Button onClick={handleJoin} size="lg" className="w-full" disabled={loading || !password}>
            {loading ? "Katiliniyor..." : "Gruba Katil"}
          </Button>
        </div>
      </div>
    );
  }

  if (mode === "create") {
    return (
      <div className="min-h-screen bg-background p-6">
        <button onClick={() => setMode("list")} className="flex items-center gap-2 text-muted-foreground mb-6 hover:text-foreground transition-colors">
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Geri</span>
        </button>

        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gold/10 flex items-center justify-center mx-auto mb-4">
            <Plus className="text-gold" size={28} />
          </div>
          <h2 className="text-xl font-bold">Yeni Halka Olustur</h2>
          <p className="text-muted-foreground text-sm mt-1">Kendi grubunuzu kurun</p>
        </div>

        <div className="space-y-4">
          <Input
            label="Halka Adi"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            placeholder="orn: Istanbul Genclik Halkasi"
          />
          <Input
            label="Grup Sifresi"
            type="password"
            value={groupPassword}
            onChange={(e) => setGroupPassword(e.target.value)}
            placeholder="Uyeler bu sifreyle katilacak"
          />
          {error && <p className="text-ehille-red text-sm text-center">{error}</p>}
          <Button onClick={handleCreate} size="lg" className="w-full" disabled={loading || !groupName || !groupPassword}>
            {loading ? "Olusturuluyor..." : "Halkayi Olustur"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="text-center mb-8">
        <Logo size={48} className="mx-auto" />
        <h1 className="mt-4 text-2xl font-bold">Halkanizi Secin</h1>
        <p className="text-muted-foreground text-sm mt-1">Bir gruba katilarak hedeflerinizi birlikte takip edin</p>
      </div>

      <div className="space-y-3 mb-6">
        {groups.map((group) => (
          <Card
            key={group.id}
            className="cursor-pointer hover:border-gold/50 transition-all active:scale-[0.98]"
            onClick={() => { setSelectedGroup(group); setMode("join"); }}
          >
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center shrink-0">
                <Users className="text-gold" size={22} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-sm truncate">{group.name}</h3>
                {group.description && (
                  <p className="text-xs text-muted-foreground truncate mt-0.5">{group.description}</p>
                )}
              </div>
              <Badge variant="secondary" className="shrink-0">
                {group.memberCount}/{group.maxMembers}
              </Badge>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="space-y-3">
        <Button variant="outline" size="lg" className="w-full gap-2" onClick={() => setMode("create")}>
          <Plus size={18} />
          Yeni Halka Olustur
        </Button>

        <Button variant="ghost" className="w-full text-muted-foreground" onClick={onSkip}>
          Simdilik atla
        </Button>
      </div>
    </div>
  );
}
